import api from './api';

export interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  disabledUsers: number;
  adminUsers: number;
}

export interface UserLicenseConfig {
  id: number;
  userId: number;
  maxDevices: number;
  maxConcurrentPlays: number;
  licenseDays: number;
  maxPlayCount: number;
  allowVideo: boolean;
  allowAudio: boolean;
  allowDocument: boolean;
  allowedContentTypes: string[];
  restrictedContentTypes: string[];
  allowPlayStartTime: string;
  allowPlayEndTime: string;
  allowWeekdays: number[];
  allowedRegions: string[];
  restrictedRegions: string[];
  allowedIPs: string[];
  restrictedIPs: string[];
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LicensedDevice {
  id: number;
  userId: number;
  deviceId: string;
  deviceName?: string;
  deviceType: string;
  deviceInfo?: object;
  lastAccessTime: string;
  lastAccessIP?: string;
  lastAccessLocation?: string;
  isActive: boolean;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LicenseUsage {
  id: number;
  userId: number;
  fileId: string;
  contentType: string;
  deviceId: string;
  action: string;
  playDuration: number;
  totalDuration: number;
  accessTime: string;
  accessIP?: string;
  accessLocation?: string;
  metadata?: object;
}

export interface ContentAccess {
  id: number;
  userId: number;
  contentId: string;
  contentType: string;
  hasAccess: boolean;
  validUntil?: string;
  accessCount: number;
  maxAccess?: number;
  restrictions?: object;
  createdAt: string;
  updatedAt: string;
}

export interface UserWithDevices extends User {
  licenseConfig: UserLicenseConfig | null;
  licensedDevices: LicensedDevice[];
  deviceStats: DeviceStats;
  recentDevice: LicensedDevice | null;
}

export interface DeviceStats {
  totalDevices: number;
  activeDevices: number;
  inactiveDevices: number;
  deviceTypes: Record<string, number>;
}

export interface DeviceSummary {
  totalDevices: number;
  activeDevices: number;
  inactiveDevices: number;
  deviceTypeStats: Record<string, { total: number; active: number; inactive: number }>;
  usersWithDevicesCount: number;
}

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  role?: string;
  status?: string;
}

export interface CreateLicenseConfigData {
  userId: number;
  maxDevices?: number;
  maxConcurrentPlays?: number;
  licenseDays?: number;
  maxPlayCount?: number;
  allowVideo?: boolean;
  allowAudio?: boolean;
  allowDocument?: boolean;
  allowedContentTypes?: string[];
  restrictedContentTypes?: string[];
  allowPlayStartTime?: string;
  allowPlayEndTime?: string;
  allowWeekdays?: number[];
  allowedRegions?: string[];
  restrictedRegions?: string[];
  allowedIPs?: string[];
  restrictedIPs?: string[];
  isActive?: boolean;
  notes?: string;
}

export interface UpdateLicenseConfigData {
  maxDevices?: number;
  maxConcurrentPlays?: number;
  licenseDays?: number;
  maxPlayCount?: number;
  allowVideo?: boolean;
  allowAudio?: boolean;
  allowDocument?: boolean;
  allowedContentTypes?: string[];
  restrictedContentTypes?: string[];
  allowPlayStartTime?: string;
  allowPlayEndTime?: string;
  allowWeekdays?: number[];
  allowedRegions?: string[];
  restrictedRegions?: string[];
  allowedIPs?: string[];
  restrictedIPs?: string[];
  isActive?: boolean;
  notes?: string;
}

export interface DeviceFilter extends Record<string, any> {
  userId?: number;
  deviceType?: string;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface UsageFilter extends Record<string, any> {
  userId?: number;
  contentType?: string;
  deviceId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  role?: string;
  status?: string;
}

// License management interfaces (License + LicenseItem)
export interface License {
  id: number;
  userId: number;
  issuedAt: string;           // ISO date string
  validUntil: string;         // ISO date string
  maxDevices: number;
  maxConcurrentPlays: number;
  maxPlayCount: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: number;
    username: string;
    email: string;
  };
  items?: LicenseItem[];
  itemsCount?: number;
}

export interface LicenseItem {
  id: number;
  licenseId: number;
  fileId: string;
  contentType: string;
  encryptedKey: string;
  signature: string;
  validUntil: string;         // ISO date string
  maxPlayCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// LicensedDevice interfaces
export interface LicensedDevice {
  id: number;
  userId: number;
  deviceId: string;
  deviceType: string;
  deviceName?: string;
  deviceInfo?: object;
  lastAccessTime: string;
  lastAccessIP?: string;
  lastAccessLocation?: string;
  isActive: boolean;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: number;
    username: string;
    email: string;
  };
}

export interface UpdateLicensedDeviceDto {
  isActive?: boolean;
  expiresAt?: string;
}

// LicenseUsage interfaces
export interface LicenseUsage {
  id: number;
  userId: number;
  fileId: string;
  contentType: string;
  deviceId: string;
  action: string;
  playDuration: number;
  totalDuration: number;
  accessIP?: string;
  accessLocation?: string;
  metadata?: object;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    username: string;
    email: string;
  };
}

export interface FilterLicenseUsagesDto {
  userId?: number;
  fileId?: string;
  contentType?: string;
  deviceId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  hasAnomaly?: boolean;
  page?: number;
  limit?: number;
}

// Content interfaces
export interface Content {
  id: number;
  fileId: string;
  filename: string;
  contentType: string;
  resolution?: string;
  duration?: number;
  bitRate?: number;
  metadata?: object;
  createdAt: string;
  updatedAt: string;
}

export interface FilterContentsDto {
  filename?: string;
  fileId?: string;
  contentType?: string;
  resolution?: string;
  durationMin?: number;
  durationMax?: number;
  bitRateMin?: number;
  bitRateMax?: number;
  page?: number;
  limit?: number;
}

export interface CreateContentDto {
  fileId: string;
  filename: string;
  contentType: string;
  resolution?: string;
  duration?: number;
  bitRate?: number;
  metadata?: any;
}

export interface UpdateContentDto {
  filename?: string;
  contentType?: string;
  resolution?: string;
  duration?: number;
  bitRate?: number;
  metadata?: any;
}

// License DTOs
export interface CreateLicenseData {
  userId: number;
  validUntil: string;
  maxDevices: number;
  maxConcurrentPlays: number;
  maxPlayCount: number;
  description?: string;
  fileIds: string[];
}

export interface UpdateLicenseData {
  validUntil?: string;
  maxDevices?: number;
  maxConcurrentPlays?: number;
  maxPlayCount?: number;
  description?: string;
}

export interface CreateLicenseItemData {
  fileId: string;
  contentType: string;
  validUntil: string;
  maxPlayCount: number;
}

export interface UpdateLicenseItemData {
  fileId?: string;
  contentType?: string;
  validUntil?: string;
  maxPlayCount?: number;
}

export const adminService = {
  // User management
  getUsers: () =>
    api.get<User[]>('/admin/users').then((res) => res.data),
  getUsersWithDevices: () =>
    api.get<User[]>('/admin/users').then((res) => res.data),
  getDeviceStats: () =>
    api.get<DeviceSummary>('/admin/device-stats').then((res) => res.data),
  createUser: (data: CreateUserData) =>
    api.post('/admin/users', data).then((res) => res.data),
  updateUser: (id: number, data: UpdateUserData) =>
    api.put(`/admin/users/${id}`, data).then((res) => res.data),
  deleteUser: (id: number) =>
    api.delete(`/admin/users/${id}`).then((res) => res.data),
  updateUserStatus: (id: number, status: string) =>
    api.patch(`/admin/users/${id}/status`, { status }).then((res) => res.data),

  // User statistics
  getStats: () =>
    api.get<UserStats>('/admin/stats').then((res) => res.data),

  // User license config management (already existed)
  getLicenseConfig: (userId: number) =>
    api.get<UserLicenseConfig>(`/license/users/${userId}/config`).then((res) => res.data),

  createLicenseConfig: (userId: number, data: CreateLicenseConfigData) => {
    const processedData = {
      ...data,
      allowedContentTypes: data.allowedContentTypes ? JSON.stringify(data.allowedContentTypes) : undefined,
      restrictedContentTypes: data.restrictedContentTypes ? JSON.stringify(data.restrictedContentTypes) : undefined,
      allowWeekdays: data.allowWeekdays ? JSON.stringify(data.allowWeekdays) : undefined,
      allowedRegions: data.allowedRegions ? JSON.stringify(data.allowedRegions) : undefined,
      restrictedRegions: data.restrictedRegions ? JSON.stringify(data.restrictedRegions) : undefined,
      allowedIPs: data.allowedIPs ? JSON.stringify(data.allowedIPs) : undefined,
      restrictedIPs: data.restrictedIPs ? JSON.stringify(data.restrictedIPs) : undefined,
    };
    return api.patch(`/admin/users/${userId}/license-config`, processedData).then((res) => res.data);
  },

  updateLicenseConfig: (userId: number, data: UpdateLicenseConfigData) => {
    const processedData = {
      ...data,
      allowedContentTypes: data.allowedContentTypes ? JSON.stringify(data.allowedContentTypes) : undefined,
      restrictedContentTypes: data.restrictedContentTypes ? JSON.stringify(data.restrictedContentTypes) : undefined,
      allowWeekdays: data.allowWeekdays ? JSON.stringify(data.allowWeekdays) : undefined,
      allowedRegions: data.allowedRegions ? JSON.stringify(data.allowedRegions) : undefined,
      restrictedRegions: data.restrictedRegions ? JSON.stringify(data.restrictedRegions) : undefined,
      allowedIPs: data.allowedIPs ? JSON.stringify(data.allowedIPs) : undefined,
      restrictedIPs: data.restrictedIPs ? JSON.stringify(data.restrictedIPs) : undefined,
    };
    return api.patch(`/admin/users/${userId}/license-config`, processedData).then((res) => res.data);
  },

  toggleLicenseConfigStatus: (userId: number) =>
    api.patch(`/admin/users/${userId}/license-config/status`, {}).then((res) => res.data),

  // Extended operations
  revokeAllDevices: (userId: number) =>
    api.patch(`/admin/users/${userId}/devices/revoke-all`).then((res) => res.data),

  resetContentAccess: (userId: number) =>
    api.patch(`/admin/users/${userId}/content-access/reset`).then((res) => res.data),

  searchDevices: (filters: DeviceFilter) =>
    api.get<LicensedDevice[]>('/admin/devices/search', { params: filters }).then((res) => res.data),

  getUserLicenseSummary: (userId: number) =>
    api.get<{
      config: UserLicenseConfig;
      devices: LicensedDevice[];
      usageStats: {
        totalUsages: number;
        activeDevices: number;
        contentAccess: number;
      };
    }>(`/admin/users/${userId}/license-summary`).then((res) => res.data),

  // New User License Config Management API (admin endpoints)
  // These use the new admin endpoints we created for comprehensive license config management
  getAllLicenseConfigs: () =>
    api.get<{
      id: number;
      userId: number;
      maxDevices: number;
      maxConcurrentPlays: number;
      licenseDays: number;
      maxPlayCount: number;
      allowVideo: boolean;
      allowAudio: boolean;
      allowDocument: boolean;
      allowedContentTypes: string;
      restrictedContentTypes: string;
      allowPlayStartTime: string;
      allowPlayEndTime: string;
      allowWeekdays: string;
      allowedRegions: string;
      restrictedRegions: string;
      allowedIPs: string;
      restrictedIPs: string;
      isActive: boolean;
      notes: string | null;
      createdAt: string;
      updatedAt: string;
      user: {
        id: number;
        username: string;
        email: string;
        status: string;
      };
    }[]>('/admin/license-configs').then((res) => res.data),

  // Get license configuration for a specific user
  getUserLicenseConfig: (userId: number) =>
    api.get<{
      id: number;
      userId: number;
      maxDevices: number;
      maxConcurrentPlays: number;
      licenseDays: number;
      maxPlayCount: number;
      allowVideo: boolean;
      allowAudio: boolean;
      allowDocument: boolean;
      allowedContentTypes: string;
      restrictedContentTypes: string;
      allowPlayStartTime: string;
      allowPlayEndTime: string;
      allowWeekdays: string;
      allowedRegions: string;
      restrictedRegions: string;
      allowedIPs: string;
      restrictedIPs: string;
      isActive: boolean;
      notes: string | null;
      createdAt: string;
      updatedAt: string;
    }>(`/admin/users/${userId}/license-config`).then((res) => res.data).catch(err => {
      // If config doesn't exist, return null
      if (err.response?.status === 404) {
        return null;
      }
      throw err;
    }),

  // Update or create license configuration for a user
  updateUserLicenseConfig: (userId: number, data: UpdateLicenseConfigData) => {
    // Convert arrays to JSON strings for backend compatibility
    const processedData = {
      ...data,
      allowedContentTypes: data.allowedContentTypes ? JSON.stringify(data.allowedContentTypes) : undefined,
      restrictedContentTypes: data.restrictedContentTypes ? JSON.stringify(data.restrictedContentTypes) : undefined,
      allowWeekdays: data.allowWeekdays ? JSON.stringify(data.allowWeekdays) : undefined,
      allowedRegions: data.allowedRegions ? JSON.stringify(data.allowedRegions) : undefined,
      restrictedRegions: data.restrictedRegions ? JSON.stringify(data.restrictedRegions) : undefined,
      allowedIPs: data.allowedIPs ? JSON.stringify(data.allowedIPs) : undefined,
      restrictedIPs: data.restrictedIPs ? JSON.stringify(data.restrictedIPs) : undefined,
    };

    return api.patch(`/admin/users/${userId}/license-config`, processedData).then((res) => res.data);
  },

  // Toggle license configuration active status
  deleteLicenseConfig: (userId: number) =>
    api.delete(`/admin/users/${userId}/license-config`).then((res) => res.data),

  createLicenseConfigWithProcessing: (userId: number, data: UpdateLicenseConfigData) => {
    const processedData = {
      ...data,
      allowedContentTypes: data.allowedContentTypes ? JSON.stringify(data.allowedContentTypes) : undefined,
      restrictedContentTypes: data.restrictedContentTypes ? JSON.stringify(data.restrictedContentTypes) : undefined,
      allowWeekdays: data.allowWeekdays ? JSON.stringify(data.allowWeekdays) : undefined,
      allowedRegions: data.allowedRegions ? JSON.stringify(data.allowedRegions) : undefined,
      restrictedRegions: data.restrictedRegions ? JSON.stringify(data.restrictedRegions) : undefined,
      allowedIPs: data.allowedIPs ? JSON.stringify(data.allowedIPs) : undefined,
      restrictedIPs: data.restrictedIPs ? JSON.stringify(data.restrictedIPs) : undefined,
    };
    return api.patch(`/admin/users/${userId}/license-config`, processedData).then((res) => res.data);
  },

  // ======== NEW LICENSE MANAGEMENT APIs ========
  // Comprehensive license management APIs (new endpoints)
  getLicenses: (page: number = 1, limit: number = 10) =>
    api.get<PaginatedResponse<License>>('/licenses', { params: { page, limit } }).then((res) => res.data),

  getLicenseById: (licenseId: number) =>
    api.get<License>(`/licenses/${licenseId}`).then((res) => res.data),

  getLicenseSummaryById: (licenseId: number) =>
    api.get<License>(`/licenses/${licenseId}/summary`).then((res) => res.data),

  createLicense: (data: CreateLicenseData) =>
    api.post<License>('/licenses', data).then((res) => res.data),

  updateLicense: (licenseId: number, data: UpdateLicenseData) =>
    api.patch<License>(`/licenses/${licenseId}`, data).then((res) => res.data),

  deleteLicense: (licenseId: number) =>
    api.delete(`/licenses/${licenseId}`).then((res) => res.data),

  getLicenseItems: (licenseId: number) =>
    api.get<LicenseItem[]>(`/licenses/${licenseId}/items`).then((res) => res.data),

  getLicenseItemsPaged: (licenseId: number, page: number = 1, limit: number = 10) =>
    api.get<PaginatedResponse<LicenseItem>>(`/licenses/${licenseId}/items/paged`, { params: { page, limit } }).then((res) => res.data),

  // License Item Management APIs
  createLicenseItem: (licenseId: number, data: CreateLicenseItemData) =>
    api.post<LicenseItem>(`/licenses/${licenseId}/items`, data).then((res) => res.data),

  updateLicenseItem: (itemId: number, data: UpdateLicenseItemData) =>
    api.patch<LicenseItem>(`/licenses/items/${itemId}`, data).then((res) => res.data),

  deleteLicenseItem: (itemId: number) =>
    api.delete(`/licenses/items/${itemId}`).then((res) => res.data),

  clearLicenseItems: (licenseId: number) =>
    api.delete<{ deletedCount: number }>(`/licenses/${licenseId}/items`).then((res) => res.data),

  // Device management methods
  getLicensedDevices: (filters?: {
    userId?: number;
    isActive?: boolean;
    deviceType?: string;
    deviceId?: string;
    expiresAfter?: string;
    expiresBefore?: string;
  }) =>
    api.get<{data: LicensedDevice[]; total: number}>('/licensed-devices', { params: filters }).then((res) => res.data),

  getLicensedDevicesByUser: (userId: number) =>
    api.get<LicensedDevice[]>(`/admin/users/${userId}/devices`).then((res) => res.data),

  revokeDevice: (deviceId: number) =>
    api.patch<LicensedDevice[]>(`/admin/devices/${deviceId}/revoke`).then(() => {}),

  // ======== NEW MODULES - LicensedDevice, LicenseUsage, Content ========

  // Licensed Device Management APIs
  getLicensedDeviceStats: () =>
    api.get<{
      totalDevices: number;
      activeDevices: number;
      expiredDevices: number;
      inactiveDevices: number;
      deviceTypeDistribution: Record<string, number>;
    }>('/licensed-devices/stats').then((res) => res.data),

  updateLicensedDevice: (deviceId: number, data: UpdateLicensedDeviceDto) =>
    api.patch<any>(`/licensed-devices/${deviceId}`, data).then((res) => res.data),

  deleteLicensedDevice: (deviceId: number) =>
    api.delete<any>(`/licensed-devices/${deviceId}`).then((res) => res.data),

  // License Usage Analytics APIs
  getLicenseUsages: (filters?: FilterLicenseUsagesDto) =>
    api.get<{data: LicenseUsage[]; total: number; page: number; limit: number; totalPages: number}>('/license-usages', { params: filters }).then((res) => res.data),

  getLicenseUsageStats: () =>
    api.get<{
      totalUsages: number;
      todayUsages: number;
      uniqueUsers: number;
      uniqueFiles: number;
      contentTypeDistribution: Array<{contentType: string; count: number}>;
      recentUsages: LicenseUsage[];
    }>('/license-usages/stats').then((res) => res.data),

  getLicenseUsageAnomalies: () =>
    api.get<Array<{
      userId: number;
      fileId: string;
      playCount: number;
      totalPlayDuration: number;
      totalFileDuration: number;
      anomalyType: string;
    }>>('/license-usages/anomalies').then((res) => res.data),

  // Content Management APIs
  getContents: (filters?: FilterContentsDto) =>
    api.get<{data: Content[]; total: number; page: number; limit: number; totalPages: number}>('/contents', { params: filters }).then((res) => res.data),

  getContentStats: () =>
    api.get<{
      totalContents: number;
      contentTypesDistribution: Record<string, number>;
    }>('/contents/stats').then((res) => res.data),

  getContentById: (contentId: number) =>
    api.get<Content>(`/contents/${contentId}`).then((res) => res.data),

  getContentByFileId: (fileId: string) =>
    api.get<Content>(`/contents/file/${fileId}`).then((res) => res.data),

  // Search content by fileId - returns paginated format for consistent UI
  searchContentByFileId: (fileId: string) =>
    api.get<Content>(`/contents/file/${fileId}`)
      .then(res => ({data: [res.data], total: 1, page: 1, limit: 10, totalPages: 1}))
      .catch(error => {
        if (error.response?.status === 404) {
          return {data: [], total: 0, page: 1, limit: 10, totalPages: 1};
        }
        throw error;
      }),

  createContent: (contentData: CreateContentDto) =>
    api.post<Content>('/contents', contentData).then((res) => res.data),

  batchCreateContents: (contentsData: CreateContentDto[]) =>
    api.post<{success: number; errors: any[]; data: Content[]}>('/contents/batch', { contents: contentsData }).then((res) => res.data),

  updateContent: (contentId: number, updateData: UpdateContentDto) =>
    api.put<Content>(`/contents/${contentId}`, updateData).then((res) => res.data),

  deleteContent: (contentId: number) =>
    api.delete<Content>(`/contents/${contentId}`).then((res) => res.data),

  batchDeleteContents: (contentIds: number[]) =>
    api.post<{ deletedCount: number }>('/contents/batch-delete', { ids: contentIds }).then((res) => res.data),

  clearContents: () =>
    api.delete<{ deletedCount: number }>('/contents').then((res) => res.data),

  importExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{
      success: number;
      errors: any[];
      data: Content[];
      totalProcessed: number;
    }>('/contents/import-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((res) => res.data);
  },

  downloadExcelTemplate: () => {
    // Create template data
    const templateData = [
      {
        '文件ID (fileId)': 'example_001',
        '文件名 (filename)': '示例视频.mp4',
        '内容类型 (contentType)': 'video',
        '分辨率 (resolution)': '1920x1080',
        '时长 (duration)': 3600,
        '码率 (bitRate)': 1024000,
        '元数据 (metadata)': '{"description": "示例视频文件"}',
      },
      {
        '文件ID (fileId)': 'example_002',
        '文件名 (filename)': '示例音频.mp3',
        '内容类型 (contentType)': 'audio',
        '分辨率 (resolution)': '',
        '时长 (duration)': 180,
        '码率 (bitRate)': 128000,
        '元数据 (metadata)': '{"artist": "示例艺术家"}',
      },
      {
        '文件ID (fileId)': 'example_003',
        '文件名 (filename)': '示例文档.pdf',
        '内容类型 (contentType)': 'document',
        '分辨率 (resolution)': '',
        '时长 (duration)': '',
        '码率 (bitRate)': '',
        '元数据 (metadata)': '{"pages": 10}',
      },
    ];

    // This will be handled by creating a Blob and triggering download
    return templateData;
  },

};
